import { useEffect, useRef, useState } from 'react';

// Respects both OS-level detection AND the explicit, persisted
// Settings > Accessibility preference (see index.css's "ACCESSIBILITY
// SETTINGS" section / App.js).
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ||
    document.documentElement.classList.contains('reduce-motion-forced'));

/**
 * Counts up (or down) from whatever value was last displayed to a new
 * target, instead of the number silently snapping. Reserved for moments a
 * fresh value genuinely just landed (`animate={true}`) -- pass
 * `animate={false}` (the default) for ordinary renders/mounts so the number
 * doesn't visibly count up every time the page is just loaded or re-rendered.
 */
const AnimatedNumber = ({ value, decimals = 0, duration = 900, animate = false, suffix = '' }) => {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!animate || prefersReducedMotion() || fromRef.current === value) {
      setDisplay(value);
      fromRef.current = value;
      return;
    }

    const from = fromRef.current;
    const to = value;
    const start = performance.now();

    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (to - from) * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration, animate]);

  return <>{display.toFixed(decimals)}{suffix}</>;
};

export default AnimatedNumber;
